/**
 * Uploads one part of a multipart upload straight to R2 with a presigned PUT.
 *
 * The whole file used to go up in a single PUT, which meant any drop killed the entire transfer
 * and left a `pending` row nobody could do anything with. The file is now cut into fixed-size
 * parts that go up independently, so a drop costs one part; R2 tracks which ones landed, which is
 * what lets an upload resume with nothing cached in the browser.
 *
 * Deliberately `XMLHttpRequest` rather than `fetch`: `fetch` has no upload-progress event, and an
 * upload that can take minutes with no feedback is not an upload the user will trust. The `Blob`
 * from `file.slice()` is a view onto the file on disk, not a copy in memory — passing it to
 * `xhr.send()` streams it, which matters when the ceiling is 5 GB.
 *
 * Three things about the request are load-bearing:
 *  - **No `Authorization` header.** The URL is already signed; adding one makes R2 reject it.
 *  - **No `Content-Type` either.** The worker signs `host` only, so any other header is unsigned
 *    and merely invites a preflight failure. A part is raw bytes; the object's type was settled
 *    when the upload was created.
 *  - **The response ETag is never read.** `/complete` builds the manifest from what R2 itself
 *    reports, so the bucket needs no CORS `ExposeHeaders` rule and a client cannot claim a part
 *    landed that did not.
 *
 * The bucket must allow `PUT` from this origin in its CORS policy — a browser PUT is preflighted,
 * and there is nothing the frontend can do about a bucket that refuses. See
 * docs/BACKEND_REQUESTS.md §2.
 */

export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0–100, rounded. `0` until the browser reports a total. */
  percent: number;
}

/** Builds the shape the progress bar reads, clamped so a resumed upload cannot report over 100%. */
export function toUploadProgress(loaded: number, total: number): UploadProgress {
  const clamped = total > 0 ? Math.min(loaded, total) : loaded;
  return {
    loaded: clamped,
    total,
    percent: total > 0 ? Math.round((clamped / total) * 100) : 0,
  };
}

export interface PutPartOptions {
  url: string;
  /** The slice of the file this part covers. */
  body: Blob;
  /** Bytes of *this part* transferred so far. */
  onProgress?: (loaded: number) => void;
  signal?: AbortSignal;
}

export class R2UploadError extends Error {
  constructor(message: string, status: number, aborted = false) {
    super(message);
    this.name = 'R2UploadError';
    this.status = status;
    this.aborted = aborted;
  }

  /** HTTP status R2 answered with, or 0 when the request never completed. */
  status: number;

  /** True when the user (or a navigation) cancelled it, so callers can stay quiet. */
  aborted: boolean;
}

/** Resolves once R2 has this part. Retrying is the caller's job — see `uploadPartBatch`. */
export function putPartToPresignedUrl({
  url,
  body,
  onProgress,
  signal,
}: PutPartOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new R2UploadError('Upload cancelled', 0, true));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    const onAbortSignal = () => xhr.abort();
    signal?.addEventListener('abort', onAbortSignal);

    const cleanup = () => signal?.removeEventListener('abort', onAbortSignal);

    xhr.upload.addEventListener('progress', (event) => {
      onProgress?.(event.loaded);
    });

    xhr.addEventListener('load', () => {
      cleanup();

      if (xhr.status >= 200 && xhr.status < 300) {
        // The progress event does not always fire a final 100% for the part.
        onProgress?.(body.size);
        resolve();
        return;
      }

      reject(new R2UploadError(
        `Storage rejected part of the upload (HTTP ${xhr.status}). ${xhr.responseText || ''}`.trim(),
        xhr.status
      ));
    });

    xhr.addEventListener('error', () => {
      cleanup();
      // A browser gives no detail here on purpose. In practice it is either the network or a
      // missing CORS policy on the bucket.
      reject(new R2UploadError(
        'The upload could not reach storage. Check your connection and try again.',
        0
      ));
    });

    xhr.addEventListener('abort', () => {
      cleanup();
      reject(new R2UploadError('Upload cancelled', 0, true));
    });

    xhr.addEventListener('timeout', () => {
      cleanup();
      reject(new R2UploadError('The upload timed out.', 0));
    });

    xhr.send(body);
  });
}
